import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Typography, Chip, CircularProgress, Paper, Alert, Stack, TextField, Button,
  Table, TableBody, TableCell, TableHead, TableRow, Link, Tooltip,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import BlockIcon from '@mui/icons-material/Block'
import { getEfMigrationChecks, triggerEfMigrationCheck } from '../../api/client'
import type { EfMigrationCheckRecord } from '../../api/types'

function CountCell({ value, warn }: { value: number; warn?: boolean }) {
  if (value === 0) return <Typography fontSize={12} color="text.disabled">—</Typography>
  return (
    <Typography fontSize={12} fontWeight={600} color={warn ? '#dc2626' : '#d97706'}>
      {value}
    </Typography>
  )
}

export default function EfMigrationPage() {
  const [owner, setOwner] = useState('skillable-engineering')
  const [repo, setRepo] = useState('lab-on-demand')
  const [pullNumber, setPullNumber] = useState('')
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['ef-migration-checks'],
    queryFn: () => getEfMigrationChecks(50),
    refetchInterval: 10_000,
  })

  const trigger = useMutation({
    mutationFn: () => triggerEfMigrationCheck(owner, repo, Number(pullNumber)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ef-migration-checks'] }),
  })

  const checks = data?.data ?? []

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h5" fontWeight={600}>EF Migration Checks</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Expand/contract safety and merge-order results for lab-on-demand's EF Core migrations.
          Every completed run from the pipeline (or triggered manually below) shows up here.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="flex-end" flexWrap="wrap">
          <TextField label="Owner" size="small" value={owner} onChange={e => setOwner(e.target.value)} sx={{ width: 200 }} />
          <TextField label="Repo" size="small" value={repo} onChange={e => setRepo(e.target.value)} sx={{ width: 200 }} />
          <TextField label="PR #" size="small" value={pullNumber} onChange={e => setPullNumber(e.target.value)} sx={{ width: 100 }} />
          <Button
            variant="contained" disableElevation
            startIcon={trigger.isPending ? <CircularProgress size={11} color="inherit" /> : <PlayArrowIcon />}
            disabled={trigger.isPending || !owner || !repo || !pullNumber}
            onClick={() => trigger.mutate()}
            sx={{ fontSize: 12, py: 0.75 }}
          >
            {trigger.isPending ? 'Running…' : 'Run Check'}
          </Button>
        </Stack>

        {trigger.isError && (
          <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => trigger.reset()}>
            {(trigger.error as Error)?.message ?? 'Check failed to run.'}
          </Alert>
        )}
        {trigger.isSuccess && trigger.data?.data && (
          <Alert
            severity={trigger.data.data.blocked ? 'error' : 'success'}
            sx={{ mt: 1.5 }}
            onClose={() => trigger.reset()}
          >
            {trigger.data.data.summary}
          </Alert>
        )}
      </Paper>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                <TableCell>When</TableCell>
                <TableCell>PR</TableCell>
                <TableCell>Verdict</TableCell>
                <TableCell align="right">Order</TableCell>
                <TableCell align="right">Blocking</TableCell>
                <TableCell align="right">Suppressed</TableCell>
                <TableCell align="right">Unresolved</TableCell>
                <TableCell align="right">Warnings</TableCell>
                <TableCell>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {checks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No checks recorded yet — they show up here once pipeline 417 runs, or you trigger one above.
                  </TableCell>
                </TableRow>
              ) : checks.map((c: EfMigrationCheckRecord) => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
                    {new Date(c.checkedAt).toLocaleString()}
                  </TableCell>

                  <TableCell>
                    <Link
                      href={`https://github.com/${c.owner}/${c.repo}/pull/${c.pullNumber}`}
                      target="_blank" rel="noopener"
                      sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}
                    >
                      {c.owner}/{c.repo}#{c.pullNumber}
                    </Link>
                  </TableCell>

                  <TableCell>
                    <Chip
                      icon={c.blocked ? <BlockIcon sx={{ fontSize: 14 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                      label={c.blocked ? 'Blocked' : 'Clean'}
                      color={c.blocked ? 'error' : 'success'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell align="right"><CountCell value={c.orderViolationCount} warn /></TableCell>
                  <TableCell align="right"><CountCell value={c.blockingCount} warn /></TableCell>
                  <TableCell align="right"><CountCell value={c.suppressedCount} /></TableCell>
                  <TableCell align="right"><CountCell value={c.unresolvedCount} /></TableCell>
                  <TableCell align="right"><CountCell value={c.warningCount} /></TableCell>

                  <TableCell sx={{ maxWidth: 320 }}>
                    <Tooltip title={c.summary} placement="top-start">
                      <Typography fontSize={12} color="text.secondary"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.summary}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  )
}
